import * as Cesium from 'cesium'
import CesiumNavigation from 'cesium-navigation-es6'
import { setPostion } from './use-cesium-map'
import { e } from '/@/utils/egms96'
import emitter from '/@/event-bus'

export default class DrawScenarioTool {
  private viewer: Cesium.Viewer;
  private store: any;
  private _navigationControl: any;
  private _originalCameraSettings: any = null;
  private _infoLocationPanel: HTMLElement | null = null;
  private _showLocationHandler: Cesium.ScreenSpaceEventHandler | null = null;
  private _dataSource: Cesium.CustomDataSource;
  private _clickModelHandler: Cesium.ScreenSpaceEventHandler | null = null;
  private _clickHandler: Cesium.ScreenSpaceEventHandler | null = null;
  constructor (viewer: Cesium.Viewer, store: any = null) {
    this.viewer = viewer
    this.store = store
    this._dataSource = new Cesium.CustomDataSource('scenarioDataSource')
    this.viewer.dataSources.add(this._dataSource)
    this._initNavigationControls()
    this._initShowLocation()
    // 保存原始相机设置
    this._saveOriginalCameraSettings()
  }

  /**
   * 初始化导航控件
   */
  private _initNavigationControls (): void {
    try {
      const options = {
        defaultResetView: Cesium.Rectangle.fromDegrees(
          113.45327513931292,
          23.185216096146178,
          120000,
        ),
        enableCompass: true,
        enableZoomControls: true,
        enableDistanceLegend: true,
        enableCompassOuterRing: true,
        moreOptions: false,
      }

      this._navigationControl = new CesiumNavigation(this.viewer, options)
      setTimeout(() => {
        const dom = document.querySelector(
          '.navigation-controls',
        ) as HTMLElement
        if (dom) dom.style.display = 'none'
      }, 300)
    } catch (error) {
      console.error('Failed to initialize navigation controls:', error)
    }
  }

  /**
   * 保存原始相机设置
   */
  private _saveOriginalCameraSettings (): void {
    const controller = this.viewer.scene.screenSpaceCameraController
    this._originalCameraSettings = {
      enableRotate: controller.enableRotate,
      enableTranslate: controller.enableTranslate,
      enableZoom: controller.enableZoom,
      enableTilt: controller.enableTilt,
      enableLook: controller.enableLook,
      depthTestAgainstTerrain: this.viewer.scene.globe.depthTestAgainstTerrain,
    }
  }

  // 节流函数
  private _throttle (fn: Function, delay: number): Function {
    let lastTime = 0
    return (...args: any[]) => {
      const now = Date.now()
      if (now - lastTime >= delay) {
        fn.apply(this, args)
        lastTime = now
      }
    }
  }

  private _isValidPosition (position: Cesium.Cartesian3): boolean {
    if (!position) return false

    try {
      const cartographic = Cesium.Cartographic.fromCartesian(position)
      if (!cartographic) return false

      return (
        !isNaN(cartographic.longitude) &&
        !isNaN(cartographic.latitude) &&
        !isNaN(cartographic.height) &&
        Math.abs(cartographic.latitude) <= Math.PI / 2 &&
        Math.abs(cartographic.longitude) <= Math.PI
      )
    } catch (error) {
      return false
    }
  }

  private _getPickPosition (screenPosition: any): Cesium.Cartesian3 | null {
    // 方法1: 使用 scene.pickPosition（优先）
    let position = this.viewer.scene.pickPosition(screenPosition)
    if (position && this._isValidPosition(position)) {
      return position
    }

    // 方法2: 使用椭球体拾取
    const ellipsoid = this.viewer.scene.globe.ellipsoid
    position = this.viewer.camera.pickEllipsoid(screenPosition, ellipsoid)
    if (position && this._isValidPosition(position)) {
      return position
    }

    // 方法3: 使用 globe.pick（备用）
    const ray = this.viewer.camera.getPickRay(screenPosition)
    if (ray) {
      position = this.viewer.scene.globe.pick(ray, this.viewer.scene)
      if (position && this._isValidPosition(position)) {
        return position
      }
    }

    return null
  }

  /**
   * 转化为真实点位经纬度
   */
  conversion (cartesian: Cesium.Cartesian3 | Cesium.Cartesian3[]): any {
    if (cartesian instanceof Array) {
      return cartesian.map((item) => this.conversion(item))
    } else {
      const cartographic = Cesium.Cartographic.fromCartesian(cartesian)
      const pointInfo: any = {}
      pointInfo.longitude = Cesium.Math.toDegrees(cartographic.longitude)
      pointInfo.latitude = Cesium.Math.toDegrees(cartographic.latitude)
      let height = cartographic.height

      try {
        const terrainHeight = this.viewer.scene.globe.getHeight(cartographic)
        if (terrainHeight !== undefined && !isNaN(terrainHeight)) {
          height = terrainHeight
        }
      } catch (e) {
        console.warn('获取地形高度失败，使用椭球高度:', e)
      }

      pointInfo.height = Math.max(0, Math.floor(height))
      return pointInfo
    }
  }

  setLoctionPostion (postion: any): void {
    setPostion(postion)
  }

  private async _getASLPoint (
    position: Cesium.Cartesian3,
    movement: { endPosition: Cesium.Cartesian2 },
  ): Promise<{ asl: string | number; hae: string | number }> {
    const pickRay = this._getPickPosition(movement.endPosition)

    if (!pickRay) return { asl: 0, hae: 0 }

    try {
      const cartographic = Cesium.Cartographic.fromCartesian(pickRay)
      const obj = {
        asl: '',
        hae: '',
      }

      // 确保高度有效
      let finalHeight = cartographic.height
      if (isNaN(finalHeight) || finalHeight < 0) {
        try {
          const terrainHeight = this.viewer.scene.globe.getHeight(cartographic)
          if (terrainHeight !== undefined && !isNaN(terrainHeight)) {
            finalHeight = terrainHeight
          } else {
            finalHeight = 0
          }
        } catch (e) {
          finalHeight = 0
        }
      }

      obj.asl = finalHeight.toFixed(1)
      const te = Cesium.Math.toDegrees(cartographic.longitude)
      const se = Cesium.Math.toDegrees(cartographic.latitude)
      const ae = e(se, te)
      obj.hae = (finalHeight + ae).toFixed(1)
      return obj
    } catch (error) {
      console.error('计算ASL高度失败:', error)
      return { asl: 0, hae: 0 }
    }
  }

  /**
   * 绘制地址坐标系
   */
  private _createInfoLocationPanel (
    longitude: string | number = '',
    latitude: string | number = '',
    height: string | number = '',
    show: boolean = false,
  ): void {
    if (this._infoLocationPanel) {
      this.viewer.container.removeChild(this._infoLocationPanel)
    }
    const Panel = document.createElement('div')
    Panel.className = 'coordinate-panel'

    const content = document.createElement('div')
    content.className = 'coordinate-content'
    Panel.appendChild(content)

    const lon = document.createElement('div')
    lon.className = 'coordinate-item'
    const lon_label = document.createElement('span')
    lon_label.className = 'label'
    lon_label.innerHTML = '经度:'
    lon.appendChild(lon_label)
    const lon_value = document.createElement('span')
    lon_value.className = 'value'
    lon_value.innerHTML = String(Number(longitude).toFixed(6))
    lon.appendChild(lon_value)
    content.appendChild(lon)

    const lat = document.createElement('div')
    lat.className = 'coordinate-item'
    const lat_label = document.createElement('span')
    lat_label.className = 'label'
    lat_label.innerHTML = '纬度:'
    lat.appendChild(lat_label)
    const lat_value = document.createElement('span')
    lat_value.className = 'value'
    lat_value.innerHTML = String(Number(latitude).toFixed(6))
    lat.appendChild(lat_value)
    content.append(lat)

    const hei = document.createElement('div')
    hei.className = 'coordinate-item'
    const hei_label = document.createElement('span')
    hei_label.className = 'label'
    hei_label.innerHTML = '海拔:'
    hei.appendChild(hei_label)
    const hei_value = document.createElement('span')
    hei_value.className = 'value'
    hei_value.innerHTML = height + 'm'
    hei.appendChild(hei_value)
    content.appendChild(hei)

    const location = document.createElement('div')
    location.className = 'coordinate-item'
    const location_label = document.createElement('span')
    location_label.className = 'label'
    location_label.innerHTML = '坐标系:'
    location.appendChild(location_label)
    const location_value = document.createElement('span')
    location_value.className = 'value'
    location_value.innerHTML = 'WGS 84'
    location.appendChild(location_value)
    content.appendChild(location)

    this._infoLocationPanel = Panel
    this._infoLocationPanel.style.display = show ? 'flex' : 'none'
    this.viewer.container.appendChild(this._infoLocationPanel)
  }

  private _initShowLocation (): void {
    // 移除已有事件处理器，防止重复绑定
    if (this._showLocationHandler) {
      this._showLocationHandler.destroy()
    }

    this._showLocationHandler = new Cesium.ScreenSpaceEventHandler(
      this.viewer.scene.canvas,
    )

    // 鼠标移动事件添加节流处理
    const handleMouseMove = this._throttle(async (moveEvent: any) => {
      const newPosition = this._getPickPosition(moveEvent.endPosition)
      if (
        newPosition &&
        Cesium.Cartesian3.equals(newPosition, Cesium.Cartesian3.ZERO) === false
      ) {
        const position = this.conversion(newPosition)

        this.setLoctionPostion(position)
        const asl = await this._getASLPoint(newPosition, moveEvent)
        this._createInfoLocationPanel(
          position.longitude,
          position.latitude,
          asl.asl,
          true,
        )
      }
    }, 500)

    // 鼠标移动显示位置
    this._showLocationHandler.setInputAction(
      handleMouseMove,
      Cesium.ScreenSpaceEventType.MOUSE_MOVE,
    )
  }

  /**
   * 实例放大
   */
  public zoomInViaNavigation (): void {
    this.viewer.camera.zoomIn()
  }

  /**
   * 实例缩小
   */
  public zoomOutViaNavigation (): void {
    this.viewer.camera.zoomOut()
  }

  public setModelHandler (type: '2d' | '3d'): void {
    if (this._clickModelHandler) {
      this._clickModelHandler.destroy()
      this._clickModelHandler = null
    }
    this._clickModelHandler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas)
    this._clickModelHandler.setInputAction((e: Cesium.ScreenSpaceEvent) => {
      const ray = this.viewer.camera.getPickRay(e.position)
      if (!ray) return
      const ellipsoid = this.viewer.scene.globe.ellipsoid
      const p = this.viewer.scene.camera.pickEllipsoid(e.position, ellipsoid)
      if (!p) return
      const positions = this.conversion(p)
      const pointPosition = [positions.longitude, positions.latitude, positions.height]
      const info = {
        color: '#2d8cf0',
        position: pointPosition,
        type: type,
        timestamp: Date.now()
      }

      try {
        const serializableInfo = JSON.parse(JSON.stringify(info))
        emitter.emit('scenarioMarkInfo', serializableInfo)
      } catch (error) {
        console.error('点数据序列化失败:', error)
        emitter.emit('scenarioMarkInfo', {
          error: '数据序列化失败',
          type: type
        })
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)
  }

  public handleClearClickHandler (): void {
    if (this._clickModelHandler) {
      this._clickModelHandler.destroy()
      this._clickModelHandler = null
    }
  }

  public getEntityById (id: string): Cesium.Entity | undefined {
    return this._dataSource.entities.getById(id)
  }

  public updatePositionById (id: string, position: Cesium.Cartesian3) {
    const entity = this.getEntityById(id)
    if (entity) {
      entity.position = position
    } else {
      console.error('未找到实体:', id)
    }
  }

  public setPolyline (list, id) {
    let entity = this.getEntityById(id)
    const positions = list.map(item => Cesium.Cartesian3.fromDegrees(Number(item[0]), Number(item[1]), Number(item[2]) || 0))
    if (entity) {
      entity.polyline.positions = positions
    } else {
      entity = this._dataSource.entities.add({
        id: id,
        polyline: {
          positions: positions,
          width: 6,
          material: new Cesium.PolylineOutlineMaterialProperty({
            color: Cesium.Color.YELLOW,
          }),
          clampToGround: true,
          classificationType: Cesium.ClassificationType.BOTH,
        }
      })
    }
    return entity
  }

  public restoreCameraSettings () {
    this._restoreCameraSettings()
  }

  /**
   * 恢复原始相机设置
   */
  private _restoreCameraSettings (): void {
    if (!this._originalCameraSettings) return

    const controller = this.viewer.scene.screenSpaceCameraController
    controller.enableRotate = this._originalCameraSettings.enableRotate
    controller.enableTranslate = this._originalCameraSettings.enableTranslate
    controller.enableZoom = this._originalCameraSettings.enableZoom
    controller.enableTilt = this._originalCameraSettings.enableTilt
    controller.enableLook = this._originalCameraSettings.enableLook
  }

  public async setDataSourcePoint (type: '1' | '2', info): Promise<void> {
    const entity = this.getEntityById(info.id)
    if (entity) {
      if (Number(type) === 1) {
        entity.billboard.image = info.downloadUrl
        entity.label.text = info.iconName
        entity.label.show = info.showNameOnMap
        entity.position = Cesium.Cartesian3.fromDegrees(info.longitude, info.latitude, info.altitude)
      } else {
        const position = Cesium.Cartesian3.fromDegrees(info.longitude, info.latitude, info.altitude)
        const headingPitchRoll = new Cesium.HeadingPitchRoll(
          Cesium.Math.toRadians(info.rotate),
          0,
          0
        )
        const orientation = Cesium.Transforms.headingPitchRollQuaternion(
          position,
          headingPitchRoll
        )
        entity.orientation = orientation
        entity.model.uri = info.downloadUrl
        entity.label.text = info.iconName
        entity.label.show = info.showNameOnMap
        entity.position = position
        entity.model.scale = info.scale
      }
    } else {
      if (Number(type) === 1) {
        const point = this._dataSource.entities.add({
          id: info.id,
          position: Cesium.Cartesian3.fromDegrees(info.longitude, info.latitude, info.altitude),
          billboard: {
            image: info.downloadUrl,
            width: 40,
            height: 40,
            scale: info.scale,
            minimumPixelSize: 0.1,
            maximumScale: 10,
            heightReference: Cesium.HeightReference.RELATIVE_TO_GROUND,
            disableDepthTestDistance: 3000000
          },
          label: {
            text: info.iconName,
            font: '14px sans-serif',
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            outlineWidth: 2,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            pixelOffset: new Cesium.Cartesian2(0, -30),
            show: info.showNameOnMap
          },
        })
      } else {
        const position = Cesium.Cartesian3.fromDegrees(info.longitude, info.latitude, info.altitude)
        const headingPitchRoll = new Cesium.HeadingPitchRoll(
          Cesium.Math.toRadians(info.rotate),
          0,
          0
        )
        const orientation = Cesium.Transforms.headingPitchRollQuaternion(
          position,
          headingPitchRoll
        )
        const model = this._dataSource.entities.add({
          id: info.id,
          orientation: orientation,
          position: position,
          model: {
            uri: info.downloadUrl,
            scale: info.scale,
            minimumPixelSize: 64,
            maximumScale: 128,
            heightReference: Cesium.HeightReference.RELATIVE_TO_GROUND,
            disableDepthTestDistance: 3000000
          },
          label: {
            text: info.iconName,
            font: '14px sans-serif',
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            outlineWidth: 2,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            pixelOffset: new Cesium.Cartesian2(0, -30),
            show: info.showNameOnMap
          },
        })
      }
    }
  }

  removeEntity (id: string): void {
    const entity = this._dataSource.entities.getById(id)
    if (entity) {
      this._dataSource.entities.remove(entity)
    }
  }

  /**
   * 销毁工具，释放资源
   */
  destroy (): void {
    if (this._showLocationHandler && !this._showLocationHandler.isDestroyed()) {
      this._showLocationHandler.destroy()
      this._showLocationHandler = null
    }

    // 移除UI元素
    if (this._infoLocationPanel && this.viewer.container.contains(this._infoLocationPanel)) {
      this.viewer.container.removeChild(this._infoLocationPanel)
      this._infoLocationPanel = null
    }

    if (this._clickModelHandler) {
      this._clickModelHandler.destroy()
      this._clickModelHandler = null
    }

    this.viewer.dataSources.remove(this._dataSource)
  }
}
